import React, { useRef } from "react";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import Image from "@tiptap/extension-image";

const FLOATS = ["left", "right", "none"];

const figureStyle = (float) => {
  if (float === "left") return { float: "left", margin: "0.25rem 1.5rem 1.25rem 0", maxWidth: "48%", width: "fit-content" };
  if (float === "right") return { float: "right", margin: "0.25rem 0 1.25rem 1.5rem", maxWidth: "48%", width: "fit-content" };
  return { float: "none", margin: "0 0 1.25rem", maxWidth: "100%", width: "fit-content" };
};

const captionStyle = { fontSize: "0.85rem", color: "#666", fontStyle: "italic", marginTop: "0.4rem", lineHeight: "1.4" };

const toCss = (style) =>
  Object.entries(style)
    .map(([key, value]) => `${key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}:${value}`)
    .join(";");

function ImageView({ node, updateAttributes, deleteNode, selected }) {
  const { src, alt, width, float, caption } = node.attrs;
  const imgRef = useRef(null);

  const startResize = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const img = imgRef.current;
    const startX = event.clientX;
    const startWidth = img.offsetWidth;
    const direction = float === "right" ? -1 : 1;
    const widthAt = (e) => Math.max(80, Math.round(startWidth + direction * (e.clientX - startX)));
    const move = (e) => { img.style.width = `${widthAt(e)}px`; };
    const up = (e) => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      img.style.width = "";
      updateAttributes({ width: widthAt(e) });
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const editMeta = () => {
    const nextAlt = window.prompt("Alt text (describes the image for screen readers and search)", alt || "");
    if (nextAlt === null) return;
    const nextCaption = window.prompt("Caption (shown under the image, leave empty for none)", caption || "");
    if (nextCaption === null) return;
    updateAttributes({ alt: nextAlt.trim(), caption: nextCaption.trim() });
  };

  return (
    <NodeViewWrapper as="figure" data-float={float} style={figureStyle(float)} className={`blog-image${selected ? " is-selected" : ""}`}>
      <img ref={imgRef} src={src} alt={alt || ""} width={width || undefined} style={{ maxWidth: "100%", height: "auto", display: "block" }} data-drag-handle draggable />
      {caption && <figcaption style={captionStyle}>{caption}</figcaption>}
      {selected && (
        <>
          <span className="blog-image-resize" onMouseDown={startResize} title="Drag to resize" />
          <div className="blog-image-tools" contentEditable={false}>
            {FLOATS.map((side) => (
              <button key={side} type="button" className={float === side ? "is-active" : ""} onClick={() => updateAttributes({ float: side })}>
                {side === "none" ? "No wrap" : `Wrap ${side}`}
              </button>
            ))}
            <button type="button" onClick={() => updateAttributes({ width: null })}>Full size</button>
            <button type="button" onClick={editMeta}>Alt / caption</button>
            <button type="button" onClick={deleteNode}>Remove</button>
          </div>
        </>
      )}
    </NodeViewWrapper>
  );
}

const BlogImage = Image.extend({
  draggable: true,

  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => (el.tagName === "FIGURE" ? el.querySelector("img") : el)?.getAttribute("width") || null,
      },
      float: {
        default: "none",
        parseHTML: (el) => (FLOATS.includes(el.getAttribute("data-float")) ? el.getAttribute("data-float") : "none"),
      },
      caption: {
        default: "",
        parseHTML: (el) => el.querySelector?.("figcaption")?.textContent?.trim() || "",
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "figure",
        getAttrs: (el) => {
          const img = el.querySelector("img");
          if (!img) return false;
          return { src: img.getAttribute("src"), alt: img.getAttribute("alt"), title: img.getAttribute("title") };
        },
      },
      { tag: "img[src]" },
    ];
  },

  renderHTML({ node }) {
    const { src, alt, width, float, caption } = node.attrs;
    const img = ["img", { src, alt: alt || "", ...(width ? { width } : {}), style: "max-width:100%;height:auto;display:block" }];
    const figure = ["figure", { "data-float": float, style: toCss(figureStyle(float)) }, img];
    if (caption) figure.push(["figcaption", { style: toCss(captionStyle) }, caption]);
    return figure;
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageView);
  },
});

export default BlogImage;
